import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Alert as RebaseAlert,
    Button as RebaseButton,
    Card as RebaseCard,
    Chip as RebaseChip,
    TextField as RebaseTextField,
    Typography as RebaseTypography,
    cls,
    defaultBorderMixin
} from "@rebasepro/ui";

import { sinceLabel } from "./_prospects_shared";

const Alert = RebaseAlert as React.ComponentType<any>;
const Button = RebaseButton as React.ComponentType<any>;
const Card = RebaseCard as React.ComponentType<any>;
const Chip = RebaseChip as React.ComponentType<any>;
const TextField = RebaseTextField as React.ComponentType<any>;
const Typography = RebaseTypography as React.ComponentType<any>;

/**
 * Answering a company that sent us a vacancy.
 *
 * A submission used to be answered by changing a dropdown in the form, and the
 * company was never told either way. Now the answer *is* the email, so the two
 * buttons are named for what the company receives:
 *
 *  - **Publicar** — the vacancy goes live, and the company gets "publicada".
 *  - **Devolver con nota** — it goes back to the company with the note, which
 *    is emailed word for word and shown on its dashboard. The company fixes it
 *    there and it returns to "Pendiente de revisión".
 *
 * Below them, what the company has actually been told: one line per row of
 * `job_notices`. The email is sent by the backend *after* the write commits
 * (`services/job-notices.ts`), so the button cannot know whether it went — it
 * says it is writing, then waits for the row, and the row says sent, failed or
 * not sent and why. Nothing on this screen claims an email went out before the
 * mail server said so.
 *
 * ── The form beside it ───────────────────────────────────────────────
 *
 * The cover sits next to the vacancy's form, and that form saves only the
 * fields that differ from the row it loaded. Publishing from here while the
 * form held unsaved edits would leave those edits to be saved later over a
 * published vacancy — or, worse, a stale status to be saved back over the
 * publication. So both actions wait until the form is clean, and after a
 * successful write the form is reset to the new status so it cannot send the
 * old one back.
 */

interface NoticeRow {
    id: string;
    kind: "received" | "published" | "returned" | string;
    status: "sent" | "failed" | "skipped" | string;
    recipient?: string | null;
    subject?: string | null;
    note?: string | null;
    public_url?: string | null;
    detail?: string | null;
    created_at?: string | null;
}

const KIND_LABEL: Record<string, string> = {
    received: "Acuse de recibo",
    published: "Aviso de publicación",
    returned: "Devuelta con nota"
};

const STATUS_LABEL: Record<string, string> = {
    sent: "Enviado",
    failed: "No salió",
    skipped: "No se envió"
};

const STATUS_COLOR: Record<string, string> = {
    sent: "greenLighter",
    failed: "redLighter",
    skipped: "grayLighter"
};

/** How long to wait for the backend to record the email before saying so. */
const WAIT_FOR_NOTICE_MS = 20_000;
const POLL_EVERY_MS = 1_500;

/**
 * The fields of a vacancy that a publish or a return changes *besides* the
 * ones the button sends — written by triggers and by the collection's
 * beforeSave. Read back after the write so the form's baseline is the row.
 */
const SERVER_WRITTEN = ["status", "return_note", "returned_at", "published_at", "closing_reminder_sent_at", "updated_at"] as const;
const DATE_FIELDS = new Set<string>(["returned_at", "published_at", "closing_reminder_sent_at", "updated_at"]);

/**
 * The form holds dates as `Date`; the client revives the ones the server tags,
 * but a plain ISO string is converted here so a timestamp does not compare as
 * changed merely because its type differs.
 */
function asFormValue(key: string, value: unknown): unknown {
    if (!DATE_FIELDS.has(key) || typeof value !== "string") return value ?? null;
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
}

interface FormexLike {
    dirty?: boolean;
    values?: Record<string, unknown>;
    resetForm?: (props?: { values?: Record<string, unknown> }) => void;
}

export interface SubmissionReviewProps {
    /** `client.data` from the panel. */
    data: any;
    jobId: string;
    values: Record<string, any>;
    /** The vacancy form's controller, when the cover is shown beside one. */
    formex?: FormexLike;
}

export function SubmissionReview({ data, jobId, values, formex }: SubmissionReviewProps) {
    const status = String(values.status ?? "");
    const posterEmail = (values.poster_email ?? "").toString().trim();

    const [notices, setNotices] = useState<NoticeRow[] | null>(null);
    const [noticesFailed, setNoticesFailed] = useState(false);
    const [returning, setReturning] = useState(false);
    const [draft, setDraft] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    /**
     * What we are waiting to see recorded: the notice kind, and the ids that
     * already existed when the button was pressed. A new id of that kind is the
     * answer — compared by id rather than by timestamp, because the browser's
     * clock and the database's need not agree.
     */
    const [waiting, setWaiting] = useState<{ kind: string; known: Set<string>; since: number } | null>(null);
    const [waitedOut, setWaitedOut] = useState(false);

    const wanted = useRef<string | null>(null);

    const load = useCallback(async (): Promise<NoticeRow[] | null> => {
        if (!data || !jobId) return null;
        wanted.current = jobId;
        try {
            const page = await data.collection("job-notices").find({
                where: { job_id: ["==", jobId] },
                orderBy: ["created_at", "desc"],
                limit: 30
            });
            if (wanted.current !== jobId) return null;
            const rows = ((page?.data ?? []) as Record<string, any>[]).map(row => ({
                ...row,
                id: String(row.id)
            })) as NoticeRow[];
            setNotices(rows);
            setNoticesFailed(false);
            return rows;
        } catch {
            if (wanted.current !== jobId) return null;
            setNoticesFailed(true);
            return null;
        }
    }, [data, jobId]);

    useEffect(() => {
        setNotices(null);
        setNoticesFailed(false);
        setReturning(false);
        setDraft("");
        setError("");
        setWaiting(null);
        setWaitedOut(false);
        void load();
    }, [jobId, load]);

    // Poll until the notice we are waiting for is recorded, or give up and say so.
    useEffect(() => {
        if (!waiting) return;
        const timer = setInterval(async () => {
            const rows = await load();
            const arrived = rows?.some(row => row.kind === waiting.kind && !waiting.known.has(row.id));
            if (arrived) {
                setWaiting(null);
                return;
            }
            if (Date.now() - waiting.since > WAIT_FOR_NOTICE_MS) {
                setWaiting(null);
                setWaitedOut(true);
            }
        }, POLL_EVERY_MS);
        return () => clearInterval(timer);
    }, [waiting, load]);

    const dirty = Boolean(formex?.dirty);
    const canAct = status === "pending_review" || status === "returned";

    const act = useCallback(async (patch: Record<string, unknown>, kind: string) => {
        setBusy(true);
        setError("");
        setWaitedOut(false);
        const known = new Set((notices ?? []).map(row => row.id));
        try {
            await data.collection("jobs").update(jobId, patch);
            // Bring the form beside this cover up to the row as it now is, so a
            // later save from it cannot send the old status back.
            //
            // "As it now is" means as the *database* has it, not as we asked:
            // the write we just made is not the whole change. The
            // `jobs_published_at_stamp` trigger sets `published_at`, beforeSave
            // stamps `returned_at` and clears the note on publish, and
            // `updated_at` moves. Merging only our own patch left the form
            // holding the old stamps as its baseline, and the next save of an
            // unrelated field sent them back — undoing "en el tablón desde".
            // So the row is read again and its server-written fields win.
            //
            // Only those fields are taken from it, not the whole row: the form
            // holds relations and other values in the shape the form builds,
            // and replacing them with the API's shape would mark untouched
            // fields dirty. If the read fails the write still succeeded, so we
            // fall back to the patch alone (the collection pins these stamps
            // server-side as well, so a stale baseline cannot overwrite them).
            if (formex?.resetForm && formex.values) {
                const next: Record<string, unknown> = { ...formex.values, ...patch };
                if (patch.status === "published") {
                    next.return_note = null;
                    next.returned_at = null;
                }
                const saved = await data.collection("jobs").findById(jobId).catch(() => undefined) as Record<string, unknown> | undefined;
                if (saved) {
                    for (const key of SERVER_WRITTEN) {
                        if (key in saved) next[key] = asFormValue(key, saved[key]);
                    }
                }
                formex.resetForm({ values: next });
            }
            setReturning(false);
            setDraft("");
            setWaiting({ kind, known, since: Date.now() });
        } catch (err: any) {
            setError(err?.message || "No se pudo guardar.");
        } finally {
            setBusy(false);
        }
    }, [data, jobId, formex, notices]);

    const publish = useCallback(() => act({ status: "published" }, "published"), [act]);
    const sendBack = useCallback(() => {
        const note = draft.trim();
        if (!note) {
            setError("Escribe qué tiene que cambiar: es lo que recibirá la empresa.");
            return;
        }
        void act({ status: "returned", return_note: note }, "returned");
    }, [act, draft]);

    const latest = useMemo(() => (notices ?? [])[0] ?? null, [notices]);

    return (
        <Card className={cls("p-4 flex flex-col gap-3", defaultBorderMixin)}>
            <div className="flex items-center gap-2 flex-wrap">
                <Typography variant="micro" color="secondary">La envió una empresa</Typography>
                {posterEmail
                    ? <Typography variant="body2">Contacto: <a href={`mailto:${posterEmail}`} className="text-primary hover:underline">{posterEmail}</a></Typography>
                    : <Typography variant="body2" color="secondary">Sin correo de contacto: no hay a quién escribir.</Typography>}
            </div>

            {canAct && (
                <div className="flex flex-col gap-2">
                    {dirty && (
                        <Alert color="warning" size="small">
                            Tienes cambios sin guardar en la ficha. Guárdalos antes de publicarla o devolverla,
                            para que la empresa reciba la vacante tal y como la dejas.
                        </Alert>
                    )}
                    {!returning ? (
                        <div className="flex items-center gap-2 flex-wrap">
                            <Button size="small" variant="filled" disabled={busy || dirty} onClick={() => void publish()}>
                                Publicar
                            </Button>
                            {status === "pending_review" && (
                                <Button size="small" variant="outlined" disabled={busy || dirty} onClick={() => { setReturning(true); setError(""); }}>
                                    Devolver con nota
                                </Button>
                            )}
                            <Typography variant="caption" color="secondary">
                                {posterEmail
                                    ? `Las dos le escriben a ${posterEmail}.`
                                    : "Ninguna de las dos puede avisarle: no dejó correo."}
                            </Typography>
                        </div>
                    ) : (
                        <div className="flex flex-col gap-2">
                            <TextField
                                size="small"
                                label="Qué tiene que cambiar para poder publicarla"
                                multiline
                                minRows={4}
                                value={draft}
                                disabled={busy}
                                autoFocus
                                onChange={(e: any) => setDraft(e.target.value)}
                            />
                            <Typography variant="caption" color="secondary">
                                Le llega tal cual, por correo, y la ve en su panel junto a la vacante. Escríbela como
                                le escribirías a esa persona.
                            </Typography>
                            <div className="flex items-center gap-2">
                                <Button size="small" variant="filled" disabled={busy || dirty || !draft.trim()} onClick={sendBack}>
                                    Devolver y enviar la nota
                                </Button>
                                <Button size="small" variant="text" disabled={busy} onClick={() => { setReturning(false); setError(""); }}>
                                    Cancelar
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {error && <Alert color="error" size="small">{error}</Alert>}
            {waiting && (
                <Typography variant="body2" color="secondary">
                    Guardado. Escribiendo a {posterEmail || "la empresa"}…
                </Typography>
            )}
            {waitedOut && (
                <Alert color="warning" size="small">
                    Guardado, pero todavía no consta el correo. Vuelve a abrir la vacante en un momento: si
                    sigue sin aparecer aquí, no salió.
                </Alert>
            )}

            <div className="flex flex-col gap-2">
                <Typography variant="subtitle2" component="h3">Lo que le hemos contado</Typography>
                {noticesFailed && (
                    <Typography variant="body2" color="secondary">No se pudo leer el registro de correos.</Typography>
                )}
                {notices?.length === 0 && (
                    <Typography variant="body2" color="secondary">No consta ningún correo a la empresa sobre esta vacante.</Typography>
                )}
                {notices?.map(row => (
                    <div key={row.id} className="flex flex-col gap-1">
                        <div className="flex items-center gap-2 flex-wrap">
                            <Typography variant="body2" className="font-medium">
                                {KIND_LABEL[row.kind] ?? row.kind}
                            </Typography>
                            <Chip size="small" colorScheme={STATUS_COLOR[row.status] ?? "grayLighter"}>
                                {STATUS_LABEL[row.status] ?? row.status}
                            </Chip>
                            <Typography variant="caption" color="secondary" title={row.created_at ?? undefined}>
                                {[row.status === "sent" ? row.recipient : null, sinceLabel(row.created_at)].filter(Boolean).join(" · ")}
                            </Typography>
                        </div>
                        {row.kind === "published" && row.status === "sent" && (
                            <Typography variant="caption" color="secondary">
                                {row.public_url
                                    ? <>Con enlace a <a href={row.public_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">la vacante</a>.</>
                                    : "Sin enlace: la página aún no existía, así que le enviamos a su panel y le dijimos que aparecerá con la próxima actualización del sitio."}
                            </Typography>
                        )}
                        {row.note && (
                            <Typography variant="body2" className="whitespace-pre-wrap break-words pl-3 border-l-2 border-surface-accent-300">
                                {row.note}
                            </Typography>
                        )}
                        {row.status !== "sent" && row.detail && (
                            <Typography variant="caption" color="secondary">{row.detail}</Typography>
                        )}
                    </div>
                ))}
                {latest?.status === "failed" && (
                    <Typography variant="caption" color="secondary">
                        El último no salió. Escríbele desde Correo para que no se quede esperando.
                    </Typography>
                )}
            </div>
        </Card>
    );
}

export default SubmissionReview;
